import React, {useCallback, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ContextualMenu, IconChevronDown, IconLanguage, OptionInterface} from '@veneer/core';

import {useClickOutside} from '../../../hooks';
import {WFCButton} from '../../WFCButton';

import useStyle from './styles';
import {LanguageOptions} from './types';

const LanguageSwitcher = ({
    setLanguage,
    getLanguages,
    currentLanguage,
}: {
    currentLanguage: string;
    setLanguage: (choice: string) => void;
    getLanguages: () => LanguageOptions;
}): JSX.Element => {
    const {t} = useTranslation(['AppSwitcher']);
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLButtonElement>(null);

    const handleChange = useCallback(
        (e: React.SyntheticEvent, option: OptionInterface) => {
            setOpen(false);
            setLanguage(option.value as string);
        },
        [setLanguage]
    );

    const handleToggle = useCallback(() => {
        setOpen(curr => !curr);
    }, []);

    const languages = getLanguages();
    const style = useStyle(open);

    const handleClickOutside = useCallback(() => {
        setOpen(false);
    }, []);

    const menuRef = useClickOutside<HTMLDivElement>(handleClickOutside);

    // eslint-disable-next-line i18next/no-literal-string
    const defaultLanguage = ['en', 'English'];
    const currentLanguageObj = languages.find(([lc]) => lc === currentLanguage);
    if (currentLanguageObj === undefined) {
        currentLanguage = defaultLanguage[0];
    }
    const currentLanguageName = (currentLanguageObj || defaultLanguage)[1];

    if (languages.length <= 1) {
        return <></>;
    }

    return (
        <ContextualMenu
            open={open}
            ref={menuRef}
            options={languages.map(([lc, name]) => ({
                value: lc,
                label: name,
            }))}
            selectedValue={currentLanguage}
            onClick={handleChange}
            anchorNode={ref.current}
        >
            <WFCButton
                ref={ref}
                id="language-switcher"
                aria-label={t('AppSwitcher:languageSelector', 'Change Language')}
                leadingIcon={<IconLanguage aria-hidden />}
                onClick={handleToggle}
                small
            >
                <div className={style.buttonText}>
                    {currentLanguageName}
                    <IconChevronDown customStyle={style.buttonChevron} />
                </div>
            </WFCButton>
        </ContextualMenu>
    );
};

export default LanguageSwitcher;
